import cx from 'classnames';
import Image from 'next/image';
import NextLink from 'next/link';

import {Button, Text} from '#component/base';
import {sprinkles} from '#style/sprinkles';

import * as css from '../CTACard/CTACard.css';

import {CTACardWideProps} from './CTACardWide.types';

import {
  CTACardWideInnerWrapperButtonTextBottom,
  CTACardWideInnerWrapperButtonTextLeft,
  CTACardWideInnerWrapperContentEnd,
  CTACardWideInnerWrapperContentEndText,
  CTACardWideInnerWrapperContentEntireRow,
  CTACardWideInnerWrapperContentEntireRowLeft,
  CTACardWideInnerWrapperContentPrimary,
  CTACardWideInnerWrapperContentPrimarySubHeading,
  CTACardWideInnerWrapperContentPrimaryText,
  CTACardWideInnerWrapperContentSecondary,
  CTACardWideInnerWrapperContentSecondaryText,
  CTACardWideInnerWrapperContentTextBottom,
  CTACardWideInnerWrapperContentTextLeftLede,
  CTACardWideInnerWrapperContentTextLeftText,
} from './CTACardWide.css';

export const CTACardWide = ({
  href = '#',
  imageUrl,
  headline,
  lede,
  variants = 'primary',
  btnText,
  subHeading,
  className,
  hasOverlay = true,
  textColor = 'white',
  buttonVariant,
  imagePosition = 'contain',
}: CTACardWideProps) => {
  const usePrimaryVariant =
    variants === 'primary' || variants === 'end' || variants === 'entireRow';
  return (
    <NextLink passHref href={href || '#'}>
      <span className={cx(css.root({variant: 'primary'}), sprinkles({colSpan: 8}), className)}>
        {hasOverlay && (
          <div
            className={css.image({
              fadeImage: true,
              variant: variants === 'burgundyBackground' ? 'burgundyBackground' : 'primary',
            })}>
            {imageUrl && (
              <Image
                alt=""
                aria-hidden
                layout="fill"
                objectFit="cover"
                src={imageUrl}
                objectPosition={imagePosition}
              />
            )}
          </div>
        )}
        <div
          className={sprinkles({
            content: usePrimaryVariant ? ['end'] : ['end', 'start'],
            display: 'flex',
            dir: 'col',
            gap: 24,
            h: 'full',
            items: 'start',
            p: [24, 24, 48],
            pos: 'relative',
          })}>
          <div
            className={sprinkles({
              flex: 'flex',
              dir: usePrimaryVariant ? ['col', 'col', 'row'] : 'col',
              h: usePrimaryVariant ? ['full', 'auto'] : 'full',
              content: usePrimaryVariant ? ['end'] : ['end', 'start'],
              items: 'start',
            })}>
            <div
              className={
                variants === 'textBottom'
                  ? CTACardWideInnerWrapperContentTextBottom
                  : variants === 'textLeft'
                  ? CTACardWideInnerWrapperContentTextLeftLede
                  : variants === 'entireRow'
                  ? CTACardWideInnerWrapperContentEntireRowLeft
                  : CTACardWideInnerWrapperContentPrimary
              }>
              {typeof headline === 'string' || typeof headline === 'number' ? (
                <Text
                  as="h1"
                  variant="heading2"
                  color={textColor}
                  className={
                    variants === 'textLeft'
                      ? CTACardWideInnerWrapperContentTextLeftText
                      : CTACardWideInnerWrapperContentPrimaryText
                  }>
                  {headline}
                </Text>
              ) : (
                headline
              )}
              {subHeading &&
                (typeof subHeading === 'string' || typeof subHeading === 'number' ? (
                  <Text
                    as="h3"
                    variant="heading3"
                    color={textColor}
                    className={CTACardWideInnerWrapperContentPrimarySubHeading}>
                    {subHeading}
                  </Text>
                ) : (
                  subHeading
                ))}
            </div>
            <div
              className={cx(
                sprinkles({
                  flex: 'flex',
                  dir: usePrimaryVariant ? ['col', 'row'] : 'col',
                  gap: 16,
                  h: ['auto', 'full'],
                }),
                variants === 'primary' && CTACardWideInnerWrapperContentSecondary,
                variants === 'end' && CTACardWideInnerWrapperContentEnd,
                variants === 'entireRow' && CTACardWideInnerWrapperContentEntireRow,
              )}>
              {typeof lede === 'string' || typeof lede === 'number' ? (
                <Text
                  as="p"
                  className={
                    variants === 'textLeft'
                      ? CTACardWideInnerWrapperContentTextLeftLede
                      : variants === 'end' || variants === 'entireRow'
                      ? CTACardWideInnerWrapperContentEndText
                      : CTACardWideInnerWrapperContentSecondaryText
                  }
                  color={textColor}>
                  {lede}
                </Text>
              ) : (
                lede
              )}
              {usePrimaryVariant ? (
                <>
                  <Button
                    className={sprinkles({display: ['none', 'flex'], self: 'end'})}
                    aria-hidden
                    as="span"
                    icon="ArrowRight"
                    variant={buttonVariant || 'primary-dark'}
                  />
                  <Button
                    className={sprinkles({
                      display: ['flex', 'none'],
                      mT: 'auto',
                    })}
                    aria-hidden
                    icon="ArrowRight"
                    as="span"
                    variant={buttonVariant || 'primary-dark'}>
                    {btnText}
                  </Button>
                </>
              ) : variants === 'textBottom' ? (
                <Button
                  className={CTACardWideInnerWrapperButtonTextBottom}
                  aria-hidden
                  as="span"
                  icon="ArrowRight"
                  variant="primary-dark">
                  Shop Now
                </Button>
              ) : variants === 'textLeft' ? (
                <Button
                  className={CTACardWideInnerWrapperButtonTextLeft}
                  aria-hidden
                  as="span"
                  icon="ArrowRight"
                  variant="primary">
                  {btnText}
                </Button>
              ) : (
                <Button
                  className={sprinkles({
                    display: 'flex',
                    mT: 'auto',
                  })}
                  aria-hidden
                  as="span"
                  icon="ArrowRight"
                  variant={buttonVariant || 'primary'}>
                  {btnText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </span>
    </NextLink>
  );
};
